import {
  normalizeEvaluacionTaller,
  type EvaluacionTallerState,
} from "../utils/otEvaluacionTaller";
import {
  CatalogCategory,
  CatalogSubcategory,
  CatalogDetail,
  CustomGarage,
  Solicitud,
  Appointment,
  EvidenciaImg,
  DetalleOT,
  SeguimientoNota,
} from "../types/ot.types";

export class OtService {

  static fetchSeguro = async (url: string, options?: RequestInit) => {
    const response = await fetch(url, options);

    const data = await response.json();

    if (data && data.status === 'error' && data.message && data.message.includes('Sesión inválida')) {
      window.location.href = '/';
      throw new Error("Sesión expirada. Redirigiendo...");
    }
    return data;
  };

  static async fetchCategories(): Promise<CatalogCategory[]> {
    try {
      const responseData = await OtService.fetchSeguro("/sittApi/obtener_categorias.php", {
        method: "GET",
      });
      return responseData.data || [];
    } catch (error) {
      console.error("Error fetching categories", error);
      return [];
    }
  }

  static async fetchSubcategories(
    idCategoria: string,
    idSubcategoria = "",
  ): Promise<CatalogSubcategory[]> {
    try {
      const data = await OtService.fetchSeguro("/api/catalogos/subcategorias.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accion: "filtered",
          idCategoria: idCategoria,
          idSubcategoria: idSubcategoria,
        }),
      });
      return data.data || data || [];
    } catch (error) {
      console.error("Error fetching subcategories", error);
      return [];
    }
  }

  static async fetchSubcategoryDetails(
    idCategoria: string,
    idSubcategoria: string,
  ): Promise<CatalogDetail[]> {
    try {
      const data = await OtService.fetchSeguro("/api/catalogos/subcategoria_detalles.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          accion: "filtered",
          idCategoria: idCategoria,
          idSubcategoria: idSubcategoria,
        }),
      });
      return data.data || data || [];
    } catch (error) {
      console.error("Error fetching subcategories details", error);
      return [];
    }
  }

  static async fetchCustomGarages(
    ciudad: string,
    bien: string,
    categoria: string,
    marca: string,
  ) {
    try {
      const response = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/obtener_talleres_cercanos.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clave_ciudad: ciudad,
            folio_bien: bien,
            clave_categoria: categoria,
            clave_marca: marca,
          }),
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching garages", error);
      return null;
    }
  }

  static async searchTaller(searchTerm: string) {
    try {
      const response = await OtService.fetchSeguro("/api/proveedores/centros.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ accion: "search", term: searchTerm }),
      });
      return response;
    } catch (error) {
      console.error("Error buscando taller", error);
      return [];
    }
  }

  static async fetchSolicitudDetalle(solicitudId: string) {
    try {
      const response = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/obtener_detalle_solicitud.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clave_solicitud_servicio: solicitudId }),
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching request details", error);
      return null;
    }
  }

  static async fetchOtDetalle(ordenId: string) {
    try {
      const response = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/obtener_detalle_orden_trabajo.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clave_orden: ordenId }),
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching request details", error);
      return null;
    }
  }

  static async fetchArchivosOT(ordenId: string): Promise<EvidenciaImg[]> {
    try {
      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/obtener_archivos_orden_trabajo.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clave_orden_trabajo: ordenId }),
        },
      );
      return responseData.data?.archivos || [];
    } catch (error) {
      console.error("Error fetching OT files", error);
      return [];
    }
  }

  static async fetchOtConceptos(ordenId: string) {
    try {
      const response = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/obtener_conceptos_orden_trabajo.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clave_orden: ordenId }),
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching concepts", error);
      return null;
    }
  }

  static async fetchAppointments(ordenId: string): Promise<Appointment[]> {
    try {
      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/obtener_citas_solicitud_servicio.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clave_orden_trabajo: ordenId }),
        },
      );
      return responseData.data?.citas || [];
    } catch (error) {
      console.error("Error fetching appointments", error);
      return [];
    }
  }

  static async createAppointment(
    clienteId: number,
    ordenId: string,
    fechaCita: string,
    proveedorId: number,
    centroId: number,
    appointmentFiles: EvidenciaImg[],
  ): Promise<Appointment[]> {
    try {
      const formData = new FormData();
      formData.append("clave_persona_cliente", clienteId.toString());
      formData.append("clave_orden_trabajo", ordenId);
      formData.append("fecha_cita", fechaCita);
      formData.append("clave_persona_proveedor", proveedorId.toString());
      formData.append("clave_centro", centroId.toString());
      if (appointmentFiles.length > 0) {
        formData.append(
          "appointment_file",
          appointmentFiles[0].file!,
          appointmentFiles[0].nombreOriginal,
        );
      }
      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/crear_cita_solicitud_servicio.php",
        {
          method: "POST",
          body: formData,
        },
      );

      if (responseData.status === "error") {
        throw new Error(
          responseData.message || "Error desconocido en el servidor",
        );
      }

      return responseData.data?.citas || [];
    } catch (error) {
      console.error("Error creating appointment", error);
      return [];
    }
  }

  static async createAppointmentEntry(
    clienteId: number,
    ordenId: string,
    fechaIngreso: string,
    evidenciasIngreso: EvidenciaImg[],
  ): Promise<Appointment[]> {
    try {
      const formData = new FormData();
      formData.append("clave_persona_cliente", clienteId.toString());
      formData.append("clave_orden_trabajo", ordenId);
      formData.append("fecha_ingreso", fechaIngreso);

      if (evidenciasIngreso.length > 0) {
        evidenciasIngreso.forEach((evidencia) => {
          if (evidencia.file) {
            formData.append(
              "evidencias_ingreso[]",
              evidencia.file,
              evidencia.nombreOriginal,
            );
          }
        });
      }

      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/registrar_ingreso_bien.php",
        {
          method: "POST",
          body: formData,
        },
      );

      if (responseData.status === "error") {
        throw new Error(
          responseData.message || "Error desconocido en el servidor",
        );
      }

      return responseData;
    } catch (error) {
      console.error("Error creating appointment entry", error);
      throw error;
    }
  }

  static async saveAprobacionesCliente(
    clienteId: number,
    ordenId: string,
    detallesOT: DetalleOT[],
    notaEtapa: string = "",
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append("clave_persona_cliente", clienteId.toString());
      formData.append("clave_orden_trabajo", ordenId);
      if (notaEtapa) formData.append("nota_etapa", notaEtapa);

      const aprobaciones = detallesOT.map((d) => ({
        clave_orden_trabajo_detalle: d.clave_orden_trabajo_detalle,
        decision: d.aprobacion_cliente === "aprobado" ? 1 : 0,
        motivo_rechazo_cliente: d.motivo_rechazo_cliente,
      }));
      formData.append("aprobaciones", JSON.stringify(aprobaciones));

      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/registrar_aprobacion_cliente.php",
        {
          method: "POST",
          body: formData,
        },
      );

      return responseData;
    } catch (error) {
      console.error("Error saving approvals", error);
      throw error;
    }
  }

  static async saveAprobacionAdmin(
    clienteId: number,
    ordenId: string,
    detallesOT: DetalleOT[],
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append("clave_persona_cliente", clienteId.toString());
      formData.append("clave_orden_trabajo", ordenId);

      const aprobaciones = detallesOT.map((d) => ({
        clave_orden_trabajo_detalle: d.clave_orden_trabajo_detalle,
        decision: d.aprobacion_admin === "aprobado" ? 1 : 0,
        motivo_rechazo: d.motivo_rechazo_admin,
        exige_evidencia: d.admin_exige_evidencia ? 1 : 0,
      }));
      formData.append("aprobaciones", JSON.stringify(aprobaciones));

      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/registrar_aprobacion_administrador.php",
        {
          method: "POST",
          body: formData,
        },
      );

      return responseData;
    } catch (error) {
      console.error("Error saving approvals", error);
      throw error;
    }
  }

  static async createDignosis(
    clienteId: number,
    ordenId: string,
    appraisalFile: EvidenciaImg,
    diagnosis: DetalleOT[],
    fechaEstimadaEntrega: string,
    confirmadoMontoPreventivo: boolean = false,
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append("clave_persona_cliente", clienteId.toString());
      formData.append("clave_orden_trabajo", ordenId);
      formData.append("diagnosis", JSON.stringify(diagnosis));
      formData.append("fecha_estimada_entrega", fechaEstimadaEntrega);
      if (confirmadoMontoPreventivo) {
        formData.append("confirmado_monto_preventivo", "1");
      }

      if (appraisalFile.file) {
        formData.append(
          "appraisal_file",
          appraisalFile.file,
          appraisalFile.nombreOriginal,
        );
      }

      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/registrar_orden_trabajo_detalle.php",
        {
          method: "POST",
          body: formData,
        },
      );

      if (responseData.status === "error") {
        throw new Error(
          responseData.message || "Error al guardar el diagnóstico",
        );
      }
      return responseData;
    } catch (error) {
      console.error("Error creating diagnosis", error);
      throw error;
    }
  }

  static async fetchAlertasPreventivo(): Promise<any[]> {
    try {
      const res = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/obtener_alertas_preventivo.php",
      );
      return res.data || [];
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  static async fetchNotasSeguimiento(
    solicitudId: string,
    ordenTrabajo: string,
  ): Promise<SeguimientoNota[]> {
    try {
      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/obtener_seguimientos.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clave_solicitud_servicio: solicitudId,
            clave_orden_trabajo: ordenTrabajo,
          }),
        },
      );
      return responseData.data?.notas || [];
    } catch (error) {
      console.error("Error fetching notes", error);
      return [];
    }
  }

  static async saveNotaSeguimiento(
    solicitudId: string,
    ordenId: string,
    comentario: string,
    keyLabel: string,
  ): Promise<boolean> {
    try {
      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/guardar_seguimiento.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clave_solicitud_servicio: solicitudId,
            clave_orden_trabajo: ordenId,
            comentarios: comentario,
            key_label: keyLabel,
          }),
        },
      );
      return responseData.status === "success";
    } catch (error) {
      console.error("Error saving note", error);
      return false;
    }
  }

  static async saveEvidenciasReparacion(
    clienteId: number,
    ordenId: string,
    evidenciasInicio: Record<number, EvidenciaImg[]>,
    evidenciasCierre: Record<number, EvidenciaImg[]>,
    excepciones: Array<{ idReal: number; descripcion: string }>,
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append("clave_persona_cliente", clienteId.toString());
      formData.append("clave_orden_trabajo", ordenId);
      formData.append("excepciones", JSON.stringify(excepciones));

      const archivosSobrevivientesInicio: Record<number, string[]> = {};
      const archivosSobrevivientesCierre: Record<number, string[]> = {};

      Object.keys(evidenciasInicio).forEach((idString) => {
        const detalleIdReal = Number.parseInt(idString);
        const archivosDelConcepto = evidenciasInicio[detalleIdReal] || [];
        archivosSobrevivientesInicio[detalleIdReal] = [];
        archivosDelConcepto.forEach((evidencia) => {
          if (evidencia.file) {
            formData.append(
              `evidencia_${detalleIdReal}[]`,
              evidencia.file,
              evidencia.nombreOriginal,
            );
          } else if (evidencia.id) {
            archivosSobrevivientesInicio[detalleIdReal].push(
              evidencia.id.toString(),
            );
          }
        });
      });

      Object.keys(evidenciasCierre).forEach((idString) => {
        const detalleIdReal = Number.parseInt(idString);
        const archivosDelConcepto = evidenciasCierre[detalleIdReal] || [];
        archivosSobrevivientesCierre[detalleIdReal] = [];
        archivosDelConcepto.forEach((evidencia) => {
          if (evidencia.file) {
            formData.append(
              `evidencia_cierre_${detalleIdReal}[]`,
              evidencia.file,
              evidencia.nombreOriginal,
            );
          } else if (evidencia.id) {
            archivosSobrevivientesCierre[detalleIdReal].push(
              evidencia.id.toString(),
            );
          }
        });
      });

      formData.append(
        "archivos_sobrevivientes",
        JSON.stringify({
          inicio: archivosSobrevivientesInicio,
          cierre: archivosSobrevivientesCierre,
        }),
      );

      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/registrar_evidencias_reparacion.php",
        { method: "POST", body: formData },
      );

      if (responseData.status === "error") throw new Error(responseData.message);

      return responseData;
    } catch (error) {
      console.error("Error saving evidences", error);
      throw error;
    }
  }

  static async saveEvidenciasCierreComplementaria(
    ordenId: string,
    evidenciasCierre: Record<number, EvidenciaImg[]>,
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append("clave_orden_trabajo", ordenId);

      const archivosSobrevivientesCierre: Record<number, string[]> = {};

      Object.keys(evidenciasCierre).forEach((idString) => {
        const detalleIdReal = Number.parseInt(idString);
        const archivosDelConcepto = evidenciasCierre[detalleIdReal] || [];
        archivosSobrevivientesCierre[detalleIdReal] = [];
        archivosDelConcepto.forEach((evidencia) => {
          if (evidencia.file) {
            formData.append(
              `evidencia_cierre_${detalleIdReal}[]`,
              evidencia.file,
              evidencia.nombreOriginal,
            );
          } else if (evidencia.id) {
            archivosSobrevivientesCierre[detalleIdReal].push(
              evidencia.id.toString(),
            );
          }
        });
      });

      formData.append(
        "archivos_sobrevivientes",
        JSON.stringify({ cierre: archivosSobrevivientesCierre }),
      );

      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/registrar_evidencias_cierre_complementaria.php",
        { method: "POST", body: formData },
      );

      if (responseData.status === "error") throw new Error(responseData.message);
      return responseData;
    } catch (error) {
      console.error("Error saving complementary closure evidence", error);
      throw error;
    }
  }

  static async savePagoControlPresupuestalServicio(
    ordenId: string,
    tipoPago: string,
    folioFactura: string,
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append("clave_orden_trabajo", ordenId);
      formData.append("tipo_pago_servicio", tipoPago);
      formData.append("folio_factura_coven", folioFactura);

      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/registrar_control_presupuestal.php",
        { method: "POST", body: formData },
      );
      if (responseData.status === "error")
        throw new Error(responseData.message);
      return responseData;
    } catch (error) {
      throw error;
    }
  }

  static async fetchDetallePagoCtrlPresupuestal(ordenId: string): Promise<any> {
    try {
      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/obtener_detalle_pago_ctl_presupuestal.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clave_orden_trabajo: ordenId }),
        },
      );
      return responseData;
    } catch (error) {
      console.error("Error fetching payment", error);
      return undefined;
    }
  }

  static async fetchEvaluacionTaller(ordenId: string): Promise<any> {
    try {
      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/obtener_evaluacion_taller.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clave_orden_trabajo: ordenId }),
        },
      );
      return responseData;
    } catch (error) {
      console.error("Error fetching payment", error);
      return undefined;
    }
  }

  static async savePagoServicio(
    ordenId: string,
    imagenesPago: EvidenciaImg[],
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append("clave_orden_trabajo", ordenId);

      imagenesPago.forEach((evidencia) => {
        if (evidencia.file)
          formData.append(
            "archivos_pago[]",
            evidencia.file,
            evidencia.nombreOriginal,
          );
      });

      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/registrar_pago_servicio.php",
        { method: "POST", body: formData },
      );
      if (responseData.status === "error")
        throw new Error(responseData.message);
      return responseData;
    } catch (error) {
      throw error;
    }
  }

  static async saveEntregaCierre(
    ordenId: string,
    imagenesEntrega: EvidenciaImg[],
    fechaEntrega: string,
    fechaTerminacion: string,
    evaluacion: EvaluacionTallerState,
    evidenciasCierre: Record<number, EvidenciaImg[]> = {},
    sinEvidenciaCierreReparacion = false,
  ): Promise<any> {
    try {
      const evaluacionNormalizada = normalizeEvaluacionTaller(evaluacion);
      const formData = new FormData();
      formData.append("clave_orden_trabajo", ordenId);
      formData.append("fechaEntrega", fechaEntrega);
      formData.append("fechaTerminacion", fechaTerminacion);
      formData.append("diagnostico", String(evaluacionNormalizada.diagnostico));
      formData.append(
        "reparacion_efectiva",
        String(evaluacionNormalizada.reparacion_efectiva),
      );
      formData.append("puntualidad", String(evaluacionNormalizada.puntualidad));
      formData.append("comunicacion", String(evaluacionNormalizada.comunicacion));
      formData.append(
        "trabajos_completados",
        String(evaluacionNormalizada.trabajos_completados),
      );
      formData.append(
        "facturacion_rapida",
        String(evaluacionNormalizada.facturacion_rapida),
      );
      formData.append("garantia", String(evaluacionNormalizada.garantia));
      formData.append(
        "comentarios_evaluacion",
        evaluacionNormalizada.comentarios,
      );

      if (sinEvidenciaCierreReparacion) {
        formData.append("sin_evidencia_cierre_reparacion", "1");
      }

      imagenesEntrega.forEach((evidencia) => {
        if (evidencia.file) {
          formData.append(
            "archivos_entrega[]",
            evidencia.file,
            evidencia.nombreOriginal,
          );
        }
      });

      const archivosSobrevivientesCierre: Record<number, string[]> = {};
      Object.keys(evidenciasCierre).forEach((idString) => {
        const detalleIdReal = Number.parseInt(idString);
        const archivosDelConcepto = evidenciasCierre[detalleIdReal] || [];
        archivosSobrevivientesCierre[detalleIdReal] = [];
        archivosDelConcepto.forEach((evidencia) => {
          if (evidencia.file) {
            formData.append(
              `evidencia_cierre_${detalleIdReal}[]`,
              evidencia.file,
              evidencia.nombreOriginal,
            );
          } else if (evidencia.id) {
            archivosSobrevivientesCierre[detalleIdReal].push(
              evidencia.id.toString(),
            );
          }
        });
      });
      formData.append(
        "archivos_sobrevivientes",
        JSON.stringify({ cierre: archivosSobrevivientesCierre }),
      );

      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/registrar_entrega_cierre.php",
        { method: "POST", body: formData },
      );
      if (responseData.status === "error")
        throw new Error(responseData.message);
      return responseData;
    } catch (error) {
      console.error("Error saving closure", error);
      throw error;
    }
  }

  static async uploadVoBoCliente(
    ordenId: string,
    detalleId: number | undefined,
    file: File,
  ): Promise<string> {
    try {
      const formData = new FormData();
      formData.append("clave_orden_trabajo", ordenId);

      if (detalleId) {
        formData.append("clave_orden_trabajo_detalle", detalleId.toString());
      } else {
        formData.append("vobo_general", "1");
      }

      formData.append("vobo_file", file, file.name);

      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/registrar_vobo_cliente.php",
        {
          method: "POST",
          body: formData,
        },
      );

      if (responseData.status === "error") {
        throw new Error(responseData.message);
      }

      return responseData.data?.url || URL.createObjectURL(file);
    } catch (error) {
      console.error("Error uploading VoBo file", error);
      throw error;
    }
  }

  static async rechazarOtEjecutivo(ordenId: string, motivo: string, tipoRechazo: number): Promise<any> {
    try {
      const formData = new FormData();
      formData.append("clave_orden_trabajo", ordenId);
      formData.append("motivo_rechazo", motivo);
      formData.append("tipo_rechazo", tipoRechazo.toString());

      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/rechazar_ot_ejecutivo.php",
        { method: "POST", body: formData }
      );

      if (responseData.status === "error") throw new Error(responseData.message);
      return responseData;
    } catch (error) {
      console.error("Error rejecting OT", error);
      throw error;
    }
  }

  static async getExpediente(ordenId: string): Promise<any> {
    try {
      const responseData = await OtService.fetchSeguro(
        `/sittApi/solicitudesServicio/obtener_expediente.php?id=${ordenId}`,
        {
          method: "GET",
        }
      );

      if (responseData.status === "error") {
        throw new Error(responseData.message || "Error al cargar el expediente");
      }

      return responseData;
    } catch (error) {
      console.error("Error fetching expediente", error);
      return null;
    }
  }

  static async saveEstatusAdministrativo(
    ordenId: string,
    isPagado: boolean,
    isFacturado: boolean,
    folioFactura: string,
    archivosPago: EvidenciaImg[],
    archivosFactura: EvidenciaImg[]
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append("clave_orden_trabajo", ordenId);
      formData.append("is_pagado", isPagado.toString());
      formData.append("is_facturado", isFacturado.toString());
      formData.append("folio_factura", folioFactura);

      archivosPago.forEach(file => file.file && formData.append("archivos_pago[]", file.file, file.nombreOriginal));
      archivosFactura.forEach(file => file.file && formData.append("archivos_factura[]", file.file, file.nombreOriginal));

      const responseData = await OtService.fetchSeguro(
        "/sittApi/solicitudesServicio/actualizar_estatus_administrativo.php",
        { method: "POST", body: formData }
      );

      if (responseData.status === "error") throw new Error(responseData.message);
      return responseData;
    } catch (error) {
      throw error;
    }
  }

  static async fetchOrdenesAdministrativas(fechaInicio: string, fechaFin: string, taller: string = ""): Promise<any[]> {
    try {
      const res = await OtService.fetchSeguro(`/sittApi/solicitudesServicio/obtener_ordenes_administrativas.php?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}&taller=${encodeURIComponent(taller)}`);
      return res.data || [];
    } catch (e) {
      console.error(e); return [];
    }
  }

  static async fetchOrdenesAdministrativasContraloria(fechaInicio: string, fechaFin: string, taller: string = ""): Promise<any[]> {
    try {
      const res = await OtService.fetchSeguro(`/sittApi/solicitudesServicio/obtener_ordenes_pendientes_contraloria.php?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}&taller=${encodeURIComponent(taller)}`);
      return res.data || [];
    } catch (e) {
      console.error(e); return [];
    }
  }

  static async fetchOrdenesAdministrativasCoordinacion(fechaInicio: string, fechaFin: string, taller: string = ""): Promise<any[]> {
    try {
      const res = await OtService.fetchSeguro(`/sittApi/solicitudesServicio/obtener_ordenes_pendientes_coordinacion.php?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}&taller=${encodeURIComponent(taller)}`);
      return res.data || [];
    } catch (e) {
      console.error(e); return [];
    }
  }

  static async saveFacturaGlobal(
    ordenesIds: number[],
    folioFactura: string,
    archivosFactura: File[],
    marcarPagado: boolean,
    archivosPago: File[]
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append("ordenes", JSON.stringify(ordenesIds));
      formData.append("folio_factura", folioFactura);
      formData.append("marcar_pagado", marcarPagado.toString());

      archivosFactura.forEach(file => formData.append("archivos_factura[]", file, file.name));
      archivosPago.forEach(file => formData.append("archivos_pago[]", file, file.name));

      const res = await OtService.fetchSeguro("/sittApi/solicitudesServicio/actualizar_factura_global.php", {
        method: "POST", body: formData
      });
      if (res.status === "error") throw new Error(res.message);
      return res;
    } catch (e) {
      throw e;
    }
  }
}
